import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-user/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import { 
  singleCountriesOptionsSelect, 
  singleGenderOptionSelect
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const AddUser = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [countryOptions, setCountryOptions] = useState('')
  const [country, setCountry] = useState({})

  const [gender, setGender] = useState(null)
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const formik = useFormik({
    initialValues: {
      user_first_name: '',
      user_last_name: '',
      phone_number: '',
      user_username: '',
      country_id: '',
      user_gender: '',
      user_email: '',
    },
    validationSchema: Yup.object().shape({
      user_first_name: Yup.string()
        .required("Please enter first name."),
      user_last_name: Yup.string()
        .required("Please enter last name."),
      user_email: Yup.string().required("Please enter email address."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllCountries()
  }, []);

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleInputValue = (values) => {
    let data = {
      username: values.user_username,
      email: values.user_email,
      password: 'A123456!',
      is_auto_verify: true
    }

    return data
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/sso/v1/users/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          let payloadProfile = {
            first_name: formik.values.user_first_name,
            last_name: formik.values.user_last_name,
            phone_number: formik.values.phone_number || null,
            country_id: formik.values.country_id || null,
            gender: formik.values.user_gender || null,
            user_id: data?.id
          }
          handleCreateProfile(payloadProfile)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleCreateProfile = async (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/sso/v1/users/profile/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          handleRequestResetPassword(data)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleRequestResetPassword = (data) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/email/send/request-reset-password`,
      data: {
        user: {
          id: data?.user_id,
          email: formik.values.user_email
        }
      }
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('User has been successfully created')
          navigate('/user/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleSelectCountry = (data) => {
    formik.setValues((values) => ({
      ...values,
      country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountry(data ? data?.value : null)
  }

  const handleSelectGender = (data) => {
    formik.setValues((values) => ({
      ...values,
      user_gender: data ? data?.value : ''
    }), false)

    setGender(data ? data : null)
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new user?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Add User</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            countryOptions={countryOptions}
            setCountry={setCountry}
            country={country}
            handleSelectCountry={handleSelectCountry}
            genderOptions={singleGenderOptionSelect()}
            gender={gender}
            handleSelectGender={handleSelectGender}
            isCreated={true}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/user/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddUser